import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;

function HomeNewsletterWidget({ overline, title, body }) {
  const [email, setEmail] = React.useState("");
  const [honeypot, setHoneypot] = React.useState("");
  const [state, setState] = React.useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("sending");
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(), source: "home_widget", honeypot,
      });
      setState("sent");
      setEmail("");
    } catch {
      setState("idle");
    }
  };

  return (
    <div className="newsletter-widget" data-testid="home-newsletter-widget">
      <div className="overline-warm mb-3">{overline}</div>
      <h2 className="font-display" style={{fontFamily:"'IM Fell English',Georgia,serif", fontSize:"1.8rem", color:"var(--warm-ink)", marginBottom:"0.5rem", fontWeight:400}}>
        {title}
      </h2>
      <p className="prose-warm mb-4" style={{maxWidth:"32rem"}}>
        {body}
      </p>
      {state === "sent" ? (
        <p className="muted" style={{fontStyle:"italic"}}>
          Thank you — please check your inbox to confirm your subscription.
        </p>
      ) : (
        <form onSubmit={submit} className="flex flex-wrap gap-2 items-end max-w-lg">
          <input type="email" value={email} required
                 onChange={(e)=>setEmail(e.target.value)}
                 placeholder="your@email"
                 className="input-warm" style={{flex:"1 1 220px"}}
                 data-testid="home-newsletter-email"/>
          <input type="text" name="company" tabIndex={-1} autoComplete="off"
                 value={honeypot} onChange={(e)=>setHoneypot(e.target.value)}
                 style={{position:"absolute", left:"-9999px", width:"1px", height:"1px"}}
                 aria-hidden="true"/>
          <button type="submit" disabled={state === "sending"} className="btn-warm-primary"
                  data-testid="home-newsletter-submit">
            {state === "sending" ? "Sending…" : "Subscribe"}
          </button>
        </form>
      )}
    </div>
  );
}

export default function Home() {
  const [content, setContent] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/site-content/home`)
      .then(r => { if (!cancelled) setContent(r.data); })
      .catch(() => { /* Render-time defaults kick in below. */ });
    return () => { cancelled = true; };
  }, []);

  // Minimal defaults for the first paint before API responds. The backend
  // returns the full set so second paint will have everything.
  const c = content || {
    hero_overline: "A novel in progress",
    hero_title: "Orc in the Machine",
    hero_lede: "",
    hero_body_html: "",
    hero_cta_primary: "About the book",
    hero_cta_secondary: "Read essays",
    hero_image_caption: "The Boggin Manuscript.",
    hero_image_caption_muted: "Illustration proof, 2026.",
    synopsis_overline: "The Book",
    synopsis_paragraphs: [],
    comps_overline: "For readers of",
    comp1_author: "", comp1_book: "", comp1_note: "",
    comp2_author: "", comp2_book: "", comp2_note: "",
    status_overline: "Status",
    status_paragraphs: [],
    newsletter_overline: "Newsletter",
    newsletter_title: "A note from the workshop.",
    newsletter_body: "",
    newsletter_image_caption: "From the workshop.",
    newsletter_image_caption_muted: "Signage department.",
  };

  return (
    <div data-testid="public-home">
      {/* Hero */}
      <section className="hero-grid gap-10 md:gap-14 items-start mb-20">
        <div className="hero-text">
          <div className="overline-warm mb-4">{c.hero_overline}</div>
          <h1 className="display-xl mb-6">{c.hero_title}</h1>
          <p className="lede mb-8">{c.hero_lede}</p>
          <p className="prose-warm"
             dangerouslySetInnerHTML={{ __html: c.hero_body_html }}/>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/about" className="btn-warm-primary" data-testid="home-cta-about">
              {c.hero_cta_primary}
            </Link>
            <Link to="/essays" className="btn-warm-ghost" data-testid="home-cta-essays">
              {c.hero_cta_secondary}
            </Link>
          </div>
        </div>

        <figure className="hero-illustration">
          <img
            src="/images/boggin-manuscript.png"
            alt="The Boggin, clutching a manuscript, surrounded by the bestiary of his world"
            className="hero-image"
          />
          <figcaption className="caption-warm mt-3">
            {c.hero_image_caption} <span className="muted">{c.hero_image_caption_muted}</span>
          </figcaption>
        </figure>
      </section>

      {/* Series synopsis */}
      <section className="synopsis mb-20">
        <div className="overline-warm mb-4">{c.synopsis_overline}</div>
        <div className="prose-warm max-w-2xl space-y-5">
          {(c.synopsis_paragraphs || []).map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </section>

      {/* Comps */}
      <section className="comps mb-20">
        <div className="overline-warm mb-4">{c.comps_overline}</div>
        <div className="comps-grid">
          <div className="comp-card">
            <div className="comp-author">{c.comp1_author}</div>
            <div className="comp-book">{c.comp1_book}</div>
            <p className="comp-note">{c.comp1_note}</p>
          </div>
          <div className="comp-card">
            <div className="comp-author">{c.comp2_author}</div>
            <div className="comp-book">{c.comp2_book}</div>
            <p className="comp-note">{c.comp2_note}</p>
          </div>
        </div>
      </section>

      {/* Status */}
      <section className="status mb-12">
        <div className="overline-warm mb-4">{c.status_overline}</div>
        <div className="prose-warm max-w-2xl space-y-3">
          {(c.status_paragraphs || []).map((p, i) => (
            <p key={i}
               className={p.muted ? "muted" : undefined}
               dangerouslySetInnerHTML={{ __html: p.html }}/>
          ))}
        </div>
      </section>

      {/* Newsletter widget */}
      <section className="mb-8 py-10 border-t border-b"
               style={{borderColor:"var(--warm-rule)"}}>
        <div className="newsletter-section-grid">
          <figure className="newsletter-illustration">
            <img
              src="/images/goblins-revolting-transparent.png"
              alt="Two goblin signwriters standing beside a bloody sign reading 'The Goblins Are Revolting' — one asks, 'Fink we shud a worded it different?'"
              className="newsletter-image"
            />
            <figcaption className="caption-warm mt-3">
              {c.newsletter_image_caption} <span className="muted">{c.newsletter_image_caption_muted}</span>
            </figcaption>
          </figure>
          <HomeNewsletterWidget
            overline={c.newsletter_overline}
            title={c.newsletter_title}
            body={c.newsletter_body}/>
        </div>
      </section>
    </div>
  );
}
